import {
  AfterViewInit,
  Component,
  computed,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  OnInit,
  ChangeDetectorRef,
  inject,
  Injector,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { toObservable } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngxs/store';
import { Subscription, fromEvent, merge } from 'rxjs';
import { debounceTime, switchMap, take, tap } from 'rxjs/operators';
import { IUserSearchResult } from '../../user-view/models/user.model';
import { SearchUsers } from '../../user-view/state/user.actions';
import { UserState } from '../../user-view/state/user.state';
import { UiTextAreaComponent } from '../../ui-elements/ui-text-area/ui-text-area.component';
import { ICommentMentionedUser, ICommentRequest, IComment } from '../model/comment.model';
import { IAttachment } from '../../attachments/models/attachments.model';
import { noEmptyTrimmedStringValidator } from '../../validators/no-empty-string-validator';
import { AddComment, UpdateComment } from '../state/comments.actions';

@Component({
  selector: 'app-comment-input',
  templateUrl: './comment-input.component.html',
  styleUrls: ['./comment-input.component.scss'],
  standalone: false,
})
export class CommentInputComponent implements OnDestroy, AfterViewInit, OnInit {
  private readonly injector = inject(Injector);

  @Input() privilege: string;
  @Input() entityId: string;
  @Input() entityType: number;
  @Input() comment?: IComment;
  @Input() isInPopup?: boolean;

  @Output() readonly editCancelled = new EventEmitter<void>();

  @ViewChild('menu') matMenu: MatMenu;
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  @ViewChild('commentTextarea') commentTextarea: UiTextAreaComponent;

  private searchUsersResultsRaw = select(UserState.searchUsersResults);

  searchUsersResults = computed(() =>
    this.searchUsersResultsRaw().map((result) => ({
      ...result,
      fullName: result.fullName.replace('  ', ' '),
    }))
  );

  control = new FormControl('', [
    Validators.required,
    noEmptyTrimmedStringValidator,
    Validators.maxLength(1000),
  ]);
  attachmentsControl = new FormControl<IAttachment[]>([], { nonNullable: true });
  mentions: ICommentMentionedUser[] = [];
  loadingActions = [AddComment, UpdateComment];

  private subscriptions = new Subscription();
  private isSearching = false;

  constructor(
    private store: Store,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.comment) {
      this.initEdit();
    }

    this.subscriptions.add(
      this.attachmentsControl.valueChanges.subscribe(() => {
        this.cdr.detectChanges();
      })
    );
  }

  ngAfterViewInit(): void {
    const textarea = this.commentTextarea?.textarea?.nativeElement;
    if (textarea && this.control) {
      // Combine value changes and cursor position changes into a single subscription
      this.subscriptions.add(
        merge(this.control.valueChanges, fromEvent(textarea, 'keyup'), fromEvent(textarea, 'click'))
          .pipe(debounceTime(200))
          .subscribe((event) => {
            if (event instanceof KeyboardEvent && event.key === 'Enter') {
              this.trigger.closeMenu();
            }

            if (!this.isSearching) {
              this.searchUsers(this.control.value || '');
            }
          })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initEdit(): void {
    if (this.comment) {
      this.control.setValue(this.comment.content);
      this.attachmentsControl.setValue(this.comment.attachments || []);
      this.mentions = [...(this.comment.mentionedUsers || [])];
    }
  }

  searchUsers(searchTerm: string): void {
    const textarea = this.commentTextarea?.textarea?.nativeElement;
    const cursorPosition = textarea?.selectionStart || 0;
    const textBeforeCursor = searchTerm.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const lastNewlineIndex = textBeforeCursor.lastIndexOf('\n');

    // If no @ or cursor is not after @, close menu and return
    if (lastAtIndex === -1 || cursorPosition <= lastAtIndex || lastNewlineIndex > lastAtIndex) {
      if (this.isSearching) {
        this.trigger.closeMenu();
      }
      return;
    }

    const searchText = textBeforeCursor.substring(lastAtIndex + 1);

    // If not enough characters or contains space, close menu and return
    if (searchText.includes(' ')) {
      if (this.isSearching) {
        this.trigger.closeMenu();
      }
      return;
    }

    this.performUserSearch(searchText);
  }

  private performUserSearch(searchText: string): void {
    this.isSearching = true;
    this.store
      .dispatch(new SearchUsers(searchText, true))
      .pipe(
        switchMap(() =>
          toObservable(this.searchUsersResults, { injector: this.injector }).pipe(take(1))
        ),
        take(1),
        tap((users: IUserSearchResult[]) => {
          this.isSearching = false;
          if (users.length > 0) {
            this.trigger.openMenu();
          } else {
            this.trigger.closeMenu();
          }
        })
      )
      .subscribe();
  }

  selectUser(user: IUserSearchResult): void {
    const currentValue = this.control.value || '';
    const lastAtIndex = currentValue.lastIndexOf('@');
    const mentionText = `@${user.fullName}`;
    // Find the end of the mention (until next space or end of string)
    const afterAt = currentValue.slice(lastAtIndex + 1);
    const firstSpace = afterAt.indexOf(' ');
    const endOfMention = firstSpace === -1 ? currentValue.length : lastAtIndex + 1 + firstSpace;

    const before = currentValue.substring(0, lastAtIndex);
    const after = currentValue.substring(endOfMention);
    const newValue = `${before}${mentionText} ${after}`;

    const start = before.length;
    const end = start + mentionText.length;

    // Remove overlapping mentions
    this.mentions = this.mentions.filter((m) => end <= m.start || start >= m.end);
    this.mentions.push({
      userId: user.id,
      name: user.fullName,
      start,
      end,
    });

    this.control.setValue(newValue);
    this.trigger.closeMenu();
  }

  addComment(): void {
    const comment: ICommentRequest = {
      entityId: this.entityId,
      content: this.control.value as string,
      mentionedUsers: this.mentions,
      attachments: this.attachmentsControl.value,
    };

    this.store.dispatch(new AddComment(this.entityType, comment, this.isInPopup));
    this.resetForm();
  }

  updateComment(): void {
    if (!this.comment) return;

    const comment: ICommentRequest = {
      entityId: this.entityId,
      content: this.control.value as string,
      mentionedUsers: this.mentions,
      attachments: this.attachmentsControl.value,
    };

    this.store.dispatch(
      new UpdateComment(this.entityType, this.comment.id, comment, this.isInPopup)
    );
    this.cancelEdit();
  }

  cancelEdit(): void {
    this.editCancelled.emit();
    this.resetForm();
  }

  addMention(): void {
    this.control.setValue(`${this.control.value || ''} @`, { emitEvent: false });
    this.performUserSearch('');
  }

  resetForm(): void {
    this.control.reset();
    this.attachmentsControl.reset();
    this.mentions = [];
  }
}
